import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';

import { ItemService } from '../item.service';
import { Item } from '../item.model';

import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { PanelModule } from 'primeng/panel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-view-item',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    ChipModule,
    PanelModule,
    ProgressSpinnerModule,
    TagModule,
    ToastModule,
  ],
  templateUrl: './view-item.component.html',
  styleUrl: './view-item.component.css',
  providers: [MessageService],
})
export class ViewItemComponent implements OnInit, OnDestroy {
  item: Item | null = null;
  isLoading = true;

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private itemService: ItemService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    const routeSub = this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (!id) {
        this.isLoading = false;
        return;
      }

      this.isLoading = true;
      this.subscriptions.add(
        this.itemService.getItemById(+id).subscribe({
          next: (item) => {
            this.item = item;
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
            console.error(err);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to load item details.',
            });
          },
        })
      );
    });

    this.subscriptions.add(routeSub);
  }

  goBackToList(): void {
    this.router.navigate(['/ai-studio/session/current', { outlets: { canvasOutlet: ['items', 'list'] } }]);
  }

  editItem(): void {
    if (!this.item) {
      return;
    }
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['items', 'edit', this.item.itemId] } },
    ]);
  }

  getStatusSeverity(status?: string): 'info' | 'success' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch ((status ?? '').toLowerCase()) {
      case 'active':
      case 'approved':
        return 'success';
      case 'inactive':
      case 'pending':
        return 'warn';
      case 'deleted':
      case 'blocked':
        return 'danger';
      default:
        return 'info';
    }
  }

  formatDate(value?: string | null): string {
    return value ? new Date(value).toLocaleString() : 'N/A';
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
